import React from 'react';
import { Box, ScrollArea, type BoxProps } from '@chakra-ui/react';
import { TRANSITION_DROPDOWN_REVEAL } from '@/components/ui/motion.ts';
import { hideNativeScrollbarCss } from '@/components/ui/scrollbar.ts';

interface DropdownMenuProps {
	isOpen: boolean;
	children: React.ReactNode;
	minW?: BoxProps['minW'];
	maxH?: BoxProps['maxH'];
}

/**
 * Absolute-positioned panel that animates open beneath a DropdownTrigger. The outer box drives the
 * max-height/opacity reveal while the scroll area owns the scroll, so long lists clip cleanly.
 */
const DropdownMenu: React.FC<DropdownMenuProps> = ({ isOpen, children, minW = '220px', maxH = '280px' }) => {
	return (
		<Box
			position="absolute"
			top="calc(100% + 4px)"
			right={0}
			minW={minW}
			bg="surface.default"
			borderRadius="2xl"
			boxShadow={isOpen ? 'lg' : 'none'}
			zIndex={100}
			maxH={isOpen ? maxH : '0'}
			opacity={isOpen ? 1 : 0}
			overflow="hidden"
			transition={TRANSITION_DROPDOWN_REVEAL}
			pointerEvents={isOpen ? 'auto' : 'none'}
		>
			<ScrollArea.Root maxH={maxH} size="xs" variant="always">
				<ScrollArea.Viewport maxH={maxH} css={hideNativeScrollbarCss}>
					{/* Keeps the last row clear of the panel's rounded corner. */}
					<ScrollArea.Content pb={2}>{children}</ScrollArea.Content>
				</ScrollArea.Viewport>
				<ScrollArea.Scrollbar position="absolute" insetBlock={1} right={1} m={0} zIndex={1} bg="transparent" />
			</ScrollArea.Root>
		</Box>
	);
};

export default DropdownMenu;
